// Admin CRUD: brands (list, add, edit, delete) — configuration / almost constant data

import { useState } from 'react';
import { useBrands } from '@/hooks/use-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Brand } from '@/lib/store-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { ImageUploadField } from '@/components/store/ImageUploadField';

export default function AdminBrands() {
  const queryClient = useQueryClient();
  const { data: brands, isLoading } = useBrands();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Brand | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logo, setLogo] = useState('');
  const { toast } = useToast();

  const saveMutation = useMutation({
    mutationFn: async () => {
      const row = { name: name.trim(), description: description.trim() || null, logo: logo.trim() || null };
      if (editing) {
        const { error } = await supabase.from('brands').update(row).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('brands').insert(row);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'lookups', 'brands'] });
      setOpen(false);
      setEditing(null);
      resetForm();
      toast({ title: editing ? 'Brand updated' : 'Brand created' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('brands').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'lookups', 'brands'] });
      toast({ title: 'Brand deleted' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  function resetForm() {
    setName('');
    setDescription('');
    setLogo('');
    setEditing(null);
  }

  /** Open modal in edit mode; form is filled from brand (name, description, logo). useBrands uses select('*') so logo is present when set in DB. */
  function openEdit(brand: Brand) {
    setEditing(brand);
    setName(brand.name);
    setDescription(brand.description ?? '');
    setLogo(brand.logo ?? '');
    setOpen(true);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Brands</h1>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogTrigger asChild>
          <Button onClick={() => { resetForm(); setEditing(null); }}>Add brand</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit brand' : 'New brand'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Brand name" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" />
            </div>
            <ImageUploadField
              label="Logo (opcional)"
              value={logo}
              onChange={setLogo}
              folder="brands"
            />
            <p className="text-sm text-muted-foreground">Sube un archivo, pega una URL para obtener y redimensionar, o pega una URL directa. Se usa como referencia al generar imagen de producto con IA.</p>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name.trim()}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      {isLoading ? (
        <p className="text-secondary">Loading...</p>
      ) : (
        <Table className="mt-6">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Logo</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(brands ?? []).map((b) => (
              <TableRow key={b.id}>
                <TableCell>{b.name}</TableCell>
                <TableCell className="text-muted-foreground">{b.description ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground">{b.logo ? 'Sí' : '—'}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(b)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(b.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
